/**
 * Design tokens — the single source of visual truth for the kit.
 * Values transcribed from engineering/ui/UI_COMPONENTS.md §1 (Consono brand).
 *
 * Components consume these for brand-specific affordances (status/confidence
 * badges, entity color-coding, brand accents). General surface/spacing/typography
 * styling goes through Fluent v9 tokens via the generated themes in ./theme.ts.
 */
import type { components } from '../api/schema';

/** The contract's semantic component enum: who | what | where | when | action. */
type ComponentType = components['schemas']['Component'];

/** Raw brand + neutral palette. */
export const palette = {
  brand: {
    /** Accent only — NEVER body text. */
    green: '#A8D600',
    /** Table headers, emphasis fills. */
    greenDark: '#3D5A00',
    /** Nav rail, dark panels. */
    dark: '#141414',
  },
  text: {
    primary: '#2D2D2D',
    mid: '#3A3A3A',
    soft: '#6B6B6B',
    muted: '#999999',
  },
  surface: {
    bg: '#F4F5F2',
    card: '#FFFFFF',
    rowAlt: '#F2F7E6',
    highlight: '#EEFACC',
    border: '#D5D8DC',
  },
} as const;

/** Status foreground/background pairs (UI_COMPONENTS.md §1 "Status"). */
export const status = {
  ok: { fg: '#3B6D11', bg: '#E7F3DA' },
  warn: { fg: '#854F0B', bg: '#FCEFD6' },
  error: { fg: '#A32D2D', bg: '#FBEAEA' },
  info: { fg: '#185FA5', bg: '#E6F1FB' },
} as const;

export type StatusKind = keyof typeof status;

/** Shape scale. Radius 8 for controls, 10 for cards; 1px borders; no shadows. */
export const radius = {
  control: '8px',
  card: '10px',
} as const;

export const typography = {
  fontFamilyBase: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'SFMono-Regular', ui-monospace, monospace",
  /** 13px base per the spec; 500 for emphasis. */
  fontSizeBase: '13px',
  fontWeightEmphasis: 500,
} as const;

/**
 * Confidence encoding — used IDENTICALLY everywhere a confidence appears
 * (UI_COMPONENTS.md §1): ≥0.85 ok, 0.65–0.85 warn, <0.65 error.
 */
export function confidenceLevel(score: number): StatusKind {
  if (score >= 0.85) return 'ok';
  if (score >= 0.65) return 'warn';
  return 'error';
}

/**
 * Entity component color-coding (EntityChip, facets, citations, drill-downs).
 * Keyed by the contract's `Component` enum (who|what|where|when|action); colors
 * are a UI decision but must stay consistent platform-wide. All fg/bg pairs meet
 * WCAG AA contrast for small text.
 */
export const entityColor: Record<ComponentType, { fg: string; bg: string; label: string }> = {
  who: { fg: '#185FA5', bg: '#E6F1FB', label: 'Who' },
  what: { fg: '#3D5A00', bg: '#F2F7E6', label: 'What' },
  where: { fg: '#854F0B', bg: '#FCEFD6', label: 'Where' },
  when: { fg: '#5B3E9A', bg: '#F0EBFB', label: 'When' },
  action: { fg: '#0F6E6E', bg: '#E1F3F3', label: 'Action' },
};
